import { el, setChildren, list, mount, unmount } from 'redom';

import { Subject } from 'rxjs';
import { concatMap } from 'rxjs/operators';

import application from 'application';
import serverInterface from 'server-interface';

import internalEventCodes from 'event-system/codes/internal';

import ScrollObserver from 'lib/scroll-observer';
import SimpleSearchControl from 'generics/components/simple-search-control/native';

import './index.scss';

class StockImagesPanel {

  constructor(configuration, dialogCallbacks) {

    this.configuration = configuration;
    this.onComplete = dialogCallbacks.onComplete;

    this.page = 1;
    this.callbackSubject = new Subject();
    this.allLoaded = false;
    this.callbackSubject.pipe(
      concatMap(() => this.loadNextPage())
    ).subscribe(this.updateList.bind(this));
    this.searchTerm = null;
    this.results = [];
    this.searchControl = new SimpleSearchControl({
      placeholder: 'Search for images',
      onChange: this.handleSearchChange.bind(this),
      onClear: this.handleSearchClear.bind(this)
    });
    this.typeSomethingToSearch = new CenteredText('Type something to search');
    this.noResults = new CenteredText('No results found');
    this.resultsSection = el('#stock-images-panel-results', this.typeSomethingToSearch);
    this.searchResults = list('#stock-images-panel-search-results.hide-scrollbar', StockImageResult, 'id', this);
    this.scrollObserver = new ScrollObserver({
      direction: ScrollObserver.directions.DOWN,
      actionableZoneFromBottom: 10,
      callback: () => this.callbackSubject.next()
    });
    this.searchResults.el.addEventListener('scroll', this.scrollObserver.callback);

    this.el = el('#stock-images-panel-main', [
      this.searchControl,
      this.resultsSection
    ]);

  }

  onmount() {

    this.searchControl.focus();

  }

  onunmount() {

    this.searchResults.el.removeEventListener('scroll', this.scrollObserver.callback);

  }

  showTypeSomethingToSearch() {

    this.searchResults.update([]);

    setChildren(this.resultsSection, [this.typeSomethingToSearch]);

  }

  showNoResults() {

    this.searchResults.update([]);

    setChildren(this.resultsSection, [this.noResults]);

  }

  async getImages() {

    try {

      const results = await serverInterface.startup.stockImages.getStockImages({
        query: this.searchTerm,
        type: this.configuration.type,
        perPage: 20,
        page: this.page
      });

      return results;

    } catch (exception) {

      application.eventSystem.internal.emit(internalEventCodes.DISPLAY_SYSTEM_TOAST_MESSAGE, {
        message: 'Please try again!',
        seconds: 5,
        type: 'error'
      });

      return [];

    }

  }

  async searchImages(searchTerm) {

    setChildren(this.resultsSection, [el('ht-spinner', { size: 'small' })]);

    this.searchTerm = searchTerm;
    this.page = 1;
    this.allLoaded = false;
    this.results = [];

    this.results = await this.getImages(searchTerm);

    if (this.results.length === 0) {
      this.showNoResults();
    } else {
      this.searchResults.update(this.results);
      setChildren(this.resultsSection, [this.searchResults]);
    }

  }

  async loadNextPage() {

    if (this.allLoaded === true) {
      return [];
    }

    this.page++;

    const spinner = el('ht-spinner', { size: 'small' });
    mount(this.resultsSection, spinner);

    const images = await this.getImages();

    unmount(this.resultsSection, spinner);

    return images;

  }

  updateList(results) {

    this.results.push(...results);

    if (results.length === 0) {
      this.allLoaded = true;
    } else {
      this.searchResults.update(this.results);
    }

  }

  handleSearchChange(searchTerm) {

    if (searchTerm === '') {
      this.showTypeSomethingToSearch();
    } else {
      this.searchImages(searchTerm);
    }

  }

  handleSearchClear() {

    this.showTypeSomethingToSearch();

  }

}

class CenteredText {

  constructor(label) {

    this.el = el('label.centerex-text', label);

  }

}

class StockImageResult {

  constructor(panel) {

    this.panel = panel;
    this.result = this.result;

    this.img = el('img.stock-image-result-image');
    this.creatorName = el('a.stock-image-result-creator-url');
    this.attributions = el('label.stock-image-result-attributions.ellipsis', [
      el('span', 'Photo by '),
      this.creatorName
    ]);

    this.el = el('.stock-image-result', {
      onclick: this.handleImageClick.bind(this)
    }, [
      this.img,
      this.attributions
    ]);

  }

  handleImageClick() {

    this.panel.onComplete(this.result);

  }

  update(result) {

    this.result = result;

    this.img.src = this.result.preview;

    const children = [this.img];

    if (this.result.user !== null) {
      this.creatorName.textContent = this.result.user.name;
      this.creatorName.href = this.result.user.link;
      children.push(this.attributions);
    }

    setChildren(this.el, children);

  }

}

export default StockImagesPanel;